<script lang="ts">
  import { onMount } from 'svelte';
  import { fontSize, currentStep, session, records, selectedChecklistId, tempSelectedRole, tempSelectedName, centerClockVisible, storageMode, storageSyncing, storageAdapter, committeeComments } from '../stores';
  import FontSizeControl from './FontSizeControl.svelte';
  import { ROLE_LABELS } from '../types';
  import type { RoleType } from '../types';

  // derived for template type-safety
  let displayedRole: RoleType | null = null;
  let displayedName: string | null = null;
  $: displayedRole = $tempSelectedRole ?? $session?.role ?? null;
  $: displayedName = $tempSelectedName || $session?.name || null;

  // header 上的時鐘文字，使用 onMount 設定 interval
  let timeText = '--:--:--';
  onMount(() => {
    const fmt = () => new Date().toLocaleString('zh-TW', {hour12: false, month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit'});
    timeText = fmt();
    const id = setInterval(() => { timeText = fmt(); }, 1000);
    return () => clearInterval(id);
  });

  function handlePrint() {
    currentStep.set('print');
  }

  function handleBack() {
    // 逐層返回：print -> checklist -> selection
    if ($currentStep === 'print') {
      currentStep.set('checklist');
      return;
    }
    // summary page 已移除，不再處理
    if ($currentStep === 'checklist') {
      // 回到 checklist 列表（保留 session.role），只清除已選的 checklist id
      selectedChecklistId.set(null);
      currentStep.set('selection');
      return;
    }
    // 其他狀態退回到 selection
    currentStep.set('selection');
  }

  function handleSelectionBack() {
    // 清除暫存的選擇角色與已選 checklist，Selection.svelte 會監聽並回到未選擇狀態
    tempSelectedRole.set(null);
    selectedChecklistId.set(null);
  }

  function handleSelectionSessionBack() {
    // 使用者在 selection 頁面但已建立 session，按返回應離開 session 回到選角
    session.set(null);
    selectedChecklistId.set(null);
    tempSelectedRole.set(null);
    currentStep.set('selection');
  }

  async function handleReset() {
    if (!confirm('確定要清除本系統的暫存資料並重新載入頁面嗎？')) return;

    const adapter = $storageAdapter;
    if (adapter) {
      try {
        await adapter.clear();
      } catch (e) {
        console.error('清除資料時發生錯誤', e);
      }
    } else {
      // Fallback: clear localStorage directly if adapter not yet initialised
      try {
        localStorage.removeItem('dmy_records');
        localStorage.removeItem('dmy_step');
        localStorage.removeItem('dmy_checklist_id');
        localStorage.removeItem('dmy_name');
      } catch (e) {
        console.error('清除 localStorage 時發生錯誤', e);
      }
    }

    // Reset in-memory stores without page reload
    session.set(null);
    records.set({});
    committeeComments.set({});
    selectedChecklistId.set(null);
    currentStep.set('selection');
  }
</script>

<header class="bg-linear-to-r from-blue-700 to-blue-900 text-white shadow-lg no-print" style="font-size: 16px !important; height: 48px; min-height: 48px; max-height: 48px; overflow: hidden;">
  <div class="container mx-auto flex flex-row items-center gap-2" style="padding: 0 12px; height: 100%; max-width: 896px;">
    <!-- Title: only show on desktop or when no role selected on mobile -->
    <div class="shrink-0 hidden sm:block">
      <h1 style="font-size: 18px; font-weight: 900; line-height: 1;">物管檢查清單</h1>
    </div>
    {#if $currentStep === 'selection' && !displayedRole && !displayedName}
      <div class="shrink-0 block sm:hidden">
        <h1 style="font-size: 16px; font-weight: 900; line-height: 1;">物管</h1>
      </div>
    {/if}

    <!-- Right side: scrollable button row, never wraps, so header stays 48px tall -->
    <div class="flex-1 flex items-center justify-end overflow-x-auto" style="gap: 10px; scrollbar-width: none;">
      {#if displayedRole || $session}
        <div class="flex items-center shrink-0" style="gap: 4px; max-width: 140px;">
          {#if displayedRole}
            <span style="font-size: 14px; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 70px;">{ROLE_LABELS[displayedRole]}</span>
          {/if}
          {#if displayedName}
            <span style="font-size: 13px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 60px; opacity: 0.9;">{displayedName}</span>
          {/if}
        </div>
      {/if}

      {#if !$centerClockVisible}
        <div class="shrink-0" id="header-time" style="font-size: 13px; font-weight: 500; white-space: nowrap; font-family: ui-monospace, monospace; font-variant-numeric: tabular-nums;">{timeText}</div>
      {/if}

      <!-- Storage mode indicator -->
      <div
        class="shrink-0 flex items-center gap-1"
        title={$storageMode === 'remote' ? '雲端同步模式' : '本機儲存模式'}
        style="cursor: default;"
      >
        {#if $storageSyncing}
          <!-- Syncing spinner -->
          <span style="font-size: 12px; animation: spin 1s linear infinite; display: inline-block;" title="同步中...">⟳</span>
        {:else if $storageMode === 'remote'}
          <span style="font-size: 10px; color: #4ade80;" title="雲端同步">●</span>
        {:else}
          <span style="font-size: 10px; color: #fbbf24;" title="本機儲存">●</span>
        {/if}
      </div>

      {#if $currentStep === 'checklist'}
        <button
          on:click={handlePrint}
          class="shrink-0 bg-white text-blue-800 rounded-md font-semibold hover:bg-gray-100"
          style="padding: 2px 8px; font-size: 13px; height: 30px; white-space: nowrap;"
        >
          印表
        </button>
      {/if}
      {#if $currentStep === 'print'}
        <button
          on:click={() => { if (typeof window !== 'undefined') window.print(); }}
          class="shrink-0 bg-green-600 text-white rounded-md font-semibold hover:bg-green-700"
          style="padding: 2px 8px; font-size: 13px; height: 30px; white-space: nowrap;"
        >
          送出列印
        </button>
      {/if}
      {#if $currentStep === 'checklist' || $currentStep === 'print' || ($currentStep === 'selection' && ($tempSelectedRole || $session))}
        <button
          on:click={() => {
            if ($currentStep === 'selection' && $tempSelectedRole) handleSelectionBack();
            else if ($currentStep === 'selection' && $session) handleSelectionSessionBack();
            else handleBack();
          }}
          class="shrink-0 bg-red-400 text-white rounded-md font-semibold hover:bg-red-500"
          style="padding: 2px 8px; font-size: 13px; height: 30px; white-space: nowrap;"
        >
          返回
        </button>
      {/if}

      <FontSizeControl />

      <button
        on:click={handleReset}
        class="shrink-0 bg-white text-blue-800 rounded-md font-semibold hover:bg-gray-100"
        style="padding: 2px 8px; font-size: 13px; height: 30px; white-space: nowrap;"
        title="清除本地儲存並重設"
      >
        重設
      </button>
    </div>
  </div>
</header>

<style>
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
</style>
